import React from "react";
import { theme } from "../../config/theme";
import type { Traffic } from "../../realtime/zoneReadings";

// Small, shared presentation atoms for "which way is it moving": a direction
// classifier, its colour tokens, a sparkline and a signed delta. Used by the
// roaming board, the map panels and the dashboards so they all agree.

export type Direction = "up" | "down" | "flat";
export const direction = (v: number): Direction => (v > 0 ? "up" : v < 0 ? "down" : "flat");
export const tone: Record<Direction, string> = { up: theme.up, down: theme.down, flat: theme.flat };
/** Colours of the traffic light (the rule itself is Zone readings' trafficOf). */
export const trafficTone: Record<Traffic, string> = {
  up: theme.up,
  steady: theme.steady,
  down: theme.down,
  none: theme.flat,
};

export const toneClass: Record<Direction, string> = {
  up: "text-ok-ink",
  down: "text-bad-ink",
  flat: "text-ink-muted",
};

/** Sparkline: enough to show the shape of the last couple of hours, no axes. */
export const Spark: React.FC<{ values: number[]; delta: number; width?: number; height?: number }> = ({
  values,
  delta,
  width = 96,
  height = 24,
}) => {
  if (values.length < 2) return <div style={{ width, height }} />;
  const mn = Math.min(...values);
  const rng = Math.max(...values) - mn || 1;
  const pts = values
    .map((v, i) => `${(i / (values.length - 1)) * width},${height - ((v - mn) / rng) * (height - 2) - 1}`)
    .join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible" aria-hidden>
      <polyline
        points={pts}
        fill="none"
        stroke={tone[direction(delta)]}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
};

export const Delta: React.FC<{ v: number; big?: boolean }> = ({ v, big }) => (
  <span className={`${toneClass[direction(v)]} ${big ? "text-2xl" : "text-base"} font-semibold tabular-nums`}>
    {v > 0 ? "▲" : v < 0 ? "▼" : "—"} {v > 0 ? "+" : ""}
    {v.toLocaleString()}
  </span>
);

/** Inline arrow + signed number for map labels and table cells. */
export const TrendInline: React.FC<{ v: number }> = ({ v }) => (
  <span className={toneClass[direction(v)]}>
    {v > 0 ? "▲" : v < 0 ? "▼" : "—"} {v > 0 ? "+" : ""}
    {v.toLocaleString()}
  </span>
);
